import { useState, useEffect, useCallback, useRef } from 'react';

export function useWakeLock() {
  const [isActive, setIsActive] = useState(false);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const shouldBeActiveRef = useRef(false);

  const isSupported =
    typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  const request = useCallback(async () => {
    if (!isSupported || !navigator.wakeLock) return;

    shouldBeActiveRef.current = true;

    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      setIsActive(true);

      wakeLockRef.current.addEventListener('release', () => {
        setIsActive(false);
        wakeLockRef.current = null;
      });
    } catch (err) {
      console.log('Wake Lock request failed:', err);
    }
  }, [isSupported]);

  const release = useCallback(async () => {
    shouldBeActiveRef.current = false;

    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
      } catch (err) {
        console.log('Wake Lock release failed:', err);
      }
      wakeLockRef.current = null;
      setIsActive(false);
    }
  }, []);

  // Re-acquire on visibility change
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === 'visible' &&
        shouldBeActiveRef.current &&
        wakeLockRef.current === null
      ) {
        try {
          if (navigator.wakeLock) {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            setIsActive(true);

            wakeLockRef.current.addEventListener('release', () => {
              setIsActive(false);
              wakeLockRef.current = null;
            });
          }
        } catch (err) {
          console.log('Wake Lock re-acquire failed:', err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      shouldBeActiveRef.current = false;
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
      }
    };
  }, []);

  return { isSupported, isActive, request, release };
}
